import { Descriptions, Flex, Input, Switch } from "antd";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useQueryClient } from "@tanstack/react-query";
import { PopconfirmButton } from "../../../../components/PopconfirmButton/PopconfirmButton";
import { ModalsContext } from "../../../../context/modals.context";
import { settingsKeys } from "../../../../modules/settings/settings.keys";
import {
  useApiSettings,
  useEditNotificationStatus,
  useRefreshApiKey,
} from "../../../../modules/settings/settings.queries";
import { toastService } from "../../../../utils/toast.service";

export const ApiSettings = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: apiSettings } = useApiSettings();
  const { setError } = useContext(ModalsContext);

  const { mutate: refreshApiKey, isPending: isRefreshingApiKey } =
    useRefreshApiKey({
      onError: () => setError(t("ERRORS.API_KEY_RENEWAL_FAILED")),
      onSettled: (data) => {
        if (data?.success) {
          toastService.success(t("TOAST.API_KEY_RENEW_SUCCESS"));

          queryClient.invalidateQueries({
            queryKey: settingsKeys.apiSettings().queryKey,
          });
        } else {
          setError(t("ERRORS.API_KEY_RENEWAL_FAILED"));
        }
      },
    });

  const { mutate: changeApiStatus, isPending: isChangingApiStatus } =
    useEditNotificationStatus({
      onError: () => setError(t("ERRORS.API_STATUS_CHANGE_FAILED")),
      onSettled: (data, isActive) => {
        if (data?.success) {
          const messageDescription = t(
            `TOAST.${isActive ? "API_ON_SUCCESS" : "API_OFF_SUCCESS"}`
          );

          toastService.success(messageDescription);
          queryClient.refetchQueries({
            queryKey: settingsKeys.apiSettings().queryKey,
            exact: false,
          });
        } else {
          setError(t("ERRORS.API_STATUS_CHANGE_FAILED"));
        }
      },
    });

  const descriptionItemsSettings = useMemo(
    () => [
      {
        key: "settings",
        label: t("SETTINGS_API.API_PODESAVANJA"),
        children: (
          <>
            <h3>{t("SETTINGS_API.API_LINK")}</h3>
            <span className="hyperLink">{apiSettings?.apiAddress}</span>
            <h3> {t("SETTINGS_API.AUTH_KEY")}</h3>
            <Flex gap={10}>
              <Input
                name="renew-api-key"
                value={apiSettings?.apiKey}
                style={{ width: "30%" }}
                readOnly
              />
              <PopconfirmButton
                buttonType="primary"
                buttonName="renew-api-key"
                buttonText={t("SETTINGS_API.OBNOVI")}
                title={t("POPCONFIRM.OBNOVI_API_KLJUC")}
                onConfirm={refreshApiKey}
                loading={isRefreshingApiKey}
              />
            </Flex>
          </>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiSettings?.apiKey, apiSettings?.apiAddress, isRefreshingApiKey]
  );

  const descriptionItemsStatus = useMemo(
    () => [
      {
        key: "status",
        label: t("SETTINGS_API.API_STATUS"),
        children: (
          <Flex align="center" gap={20}>
            <p>{t("COMMON.AKTIVNO")}</p>
            <Switch
              checked={apiSettings?.aktivno}
              onChange={(value: boolean) => changeApiStatus(value)}
              loading={isChangingApiStatus}
            />
          </Flex>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiSettings?.aktivno, isChangingApiStatus]
  );

  return (
    <Flex gap={20} vertical>
      <Descriptions
        items={descriptionItemsSettings}
        layout="vertical"
        bordered
      />
      <Descriptions items={descriptionItemsStatus} layout="vertical" bordered />
    </Flex>
  );
};
